import { Button } from "$components/button/button";
import { Card, CardColors, CardTitle } from "$components/card";
import { Box, Menu, MenuItem, styled, Typography } from "@material-ui/core";
import { Fragment, type MouseEvent, type ReactNode } from "react";

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  color: theme.palette.text.primary,
  ...theme.typography.body1,
  "& svg": {
    width: 16,
    height: 16,
    color: theme.palette.primary.main,
  },
}));

type Recommendation = {
  id: string;
  label: ReactNode;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
  menuItems?: {
    key: string;
    onClick: () => void;
    label: string;
    adornment: ReactNode;
  }[];
};

export const RecommendationsBox: React.FC<{
  TitleText: ReactNode;
  SubtitleText?: ReactNode;
  recommendations: Recommendation[];
  handleClick: (e: MouseEvent<HTMLButtonElement>, id: string) => void;
  titleAdornment?: ReactNode;
  menuAnchorEl?: HTMLElement | null;
  menuTrackingId?: string | null;
  menuHandleClose?: () => void;
}> = ({
  TitleText,
  SubtitleText,
  recommendations,
  handleClick,
  titleAdornment,
  menuAnchorEl,
  menuTrackingId,
  menuHandleClose,
}) => {
  return (
    <Card color={CardColors.TRANSPARENT}>
      <TitleContainer>
        <CardTitle>{TitleText}</CardTitle>
        {titleAdornment && titleAdornment}
      </TitleContainer>
      {SubtitleText && SubtitleText}
      <ActionButtonsContainer>
        {recommendations.map((recommendation) => (
          <Fragment key={recommendation.id}>
            <ActionButton
              variant="contained"
              color="secondary"
              onClick={(e) => handleClick(e, recommendation.id)}
              endIcon={recommendation.endIcon && recommendation.endIcon}
              startIcon={recommendation.startIcon && recommendation.startIcon}
            >
              {recommendation.label}
            </ActionButton>
            {!!recommendation.menuItems?.length && (
              <Menu
                id={menuTrackingId}
                open={!!menuAnchorEl && menuTrackingId === recommendation.id}
                anchorEl={menuAnchorEl}
                onClose={menuHandleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                getContentAnchorEl={null}
                elevation={2}
              >
                {recommendation.menuItems.map((menuItem) => (
                  <StyledMenuItem key={menuItem.key} onClick={menuItem.onClick}>
                    {menuItem.adornment && menuItem.adornment}
                    <Typography>{menuItem.label}</Typography>
                  </StyledMenuItem>
                ))}
              </Menu>
            )}
          </Fragment>
        ))}
      </ActionButtonsContainer>
    </Card>
  );
};
